import { useState, useEffect, useRef } from 'react'
import { MessageCircle, Send, Paperclip, Download, Eye, ChevronDown, ChevronUp, Loader2, FileUp } from 'lucide-react'
import { useChat } from '../../hooks/useChat.jsx'
import api from '../../services/api'
import FileViewerModal from '../common/FileViewerModal.jsx'
import { useTranslation } from 'react-i18next'

/**
 * MilestoneChat — inline collapsible thread under a milestone card.
 * Used by PM, Consultant, Admin to discuss milestone-specific issues.
 * Perfect for back-and-forth after a rejection or revision request.
 */
export default function MilestoneChat({ milestoneId, projectId, milestoneName, autoOpen = false, readOnly = false }) {
  const { t } = useTranslation('assistant')
  // Promoting an attachment into a deliverable. The file is already on the server, so this
  // asks for a title and nothing else -- there is no second upload to wait for.
  const [promote, setPromote] = useState(null)      // the message being promoted
  const [promoteTitle, setPromoteTitle] = useState('')
  const [promoteBusy, setPromoteBusy] = useState(false)
  const [promoteNote, setPromoteNote] = useState(null)

  const submitFromChat = async () => {
    if (!promote || !promoteTitle.trim()) return
    setPromoteBusy(true); setPromoteNote(null)
    try {
      const { data } = await api.post('/deliverables/from-chat', {
        message_id: promote.id,
        milestone_id: milestoneId,
        title: promoteTitle.trim(),
      })
      setPromoteNote({ ok: true, text: data.detail })
      setPromote(null); setPromoteTitle('')
    } catch (e) {
      setPromoteNote({ ok: false, text: e.response?.data?.detail || 'Could not submit it as a deliverable.' })
    } finally {
      setPromoteBusy(false)
    }
  }
  const [open, setOpen] = useState(false)
  const [text, setText] = useState('')
  const [file, setFile] = useState(null)
  const fileRef = useRef()
  const bottomRef = useRef()
  const rootRef = useRef()
  const [viewing, setViewing] = useState(null)   // the attachment open in the viewer

  const { messages, loading, sending, error, sendMessage, markRead } = useChat('milestone', milestoneId, open)

  // Arriving from a chat notification (?milestone=<id>) → auto-open + scroll into view.
  useEffect(() => {
    if (autoOpen) {
      setOpen(true)
      setTimeout(() => rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 300)
    }
  }, [autoOpen])

  useEffect(() => {
    if (open) {
      markRead(projectId)
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80)
    }
  }, [open, messages.length])

  const send = () => {
    if (!text.trim() && !file) return
    sendMessage(text, file)
    setText(''); setFile(null)
    if (fileRef.current) fileRef.current.value = ''
  }

  // View opens the in-app viewer — a new tab with a blob is how a Word attachment ends up in
  // the downloads folder instead of on screen. Download is unchanged.
  const openFile = async (msg, mode = 'attachment') => {
    if (mode === 'inline') { setViewing(msg); return }
    try {
      const r = await api.get(`/chat/file/${msg.id}`, {
        params: { disposition: mode },
        responseType: 'blob',
      })
      const url = URL.createObjectURL(r.data)
      const a = document.createElement('a')
      a.href = url; a.download = msg.file_name || 'file'
      document.body.appendChild(a); a.click()
      document.body.removeChild(a)
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch { alert('Could not open file') }
  }

  const unread = open ? 0 : messages.filter(m => !m.is_mine).length

  return (
    <div ref={rootRef} className="border-t border-gray-100">
      {/* Toggle button */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-2 text-xs text-gray-500 hover:bg-gray-50 transition"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        <span className="font-medium">{t('milestone_chat')}</span>
        {messages.length > 0 && (
          <span className="text-gray-400">({messages.length} message{messages.length !== 1 ? 's' : ''})</span>
        )}
        {unread > 0 && (
          <span className="ml-auto bg-blue-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unread}</span>
        )}
        <span className="ml-auto">
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      </button>

      {open && (
        <div className="bg-gray-50 border-t border-gray-100">
          {/* Who can see this thread */}
          <div className="px-4 pt-2 pb-1 flex items-center gap-1.5 flex-wrap">
            <span className="text-[10px] text-gray-400">{t('visible_to')}</span>
            {[
              { role: 'PM', color: 'bg-blue-100 text-blue-700' },
              { role: 'Consultant', color: 'bg-green-100 text-green-700' },
              { role: 'Admin', color: 'bg-purple-100 text-purple-700' },
            ].map(p => (
              <span key={p.role} className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${p.color}`}>
                {p.role}
              </span>
            ))}
          </div>
          {/* Messages list */}
          <div className="max-h-64 overflow-y-auto px-4 py-3 space-y-2">
            {loading ? (
              <div className="flex justify-center py-4"><Loader2 className="w-4 h-4 animate-spin text-blue-500" /></div>
            ) : messages.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">
                No messages yet. Use this thread to discuss this milestone.
              </p>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className={`flex gap-2 ${msg.is_mine ? 'flex-row-reverse' : ''}`}>
                  {/* Avatar */}
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0 ${
                    msg.sender?.role === 'Admin' ? 'bg-purple-500' :
                    msg.sender?.role === 'Project Manager' ? 'bg-blue-500' :
                    msg.sender?.role === 'Consultant' ? 'bg-green-500' : 'bg-gray-400'
                  }`}>
                    {(msg.sender?.full_name || '?').slice(0, 2).toUpperCase()}
                  </div>

                  <div className={`flex flex-col max-w-[80%] ${msg.is_mine ? 'items-end' : ''}`}>
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`text-[10px] font-semibold text-gray-600 ${msg.is_mine ? 'order-last' : ''}`}>
                        {msg.sender?.full_name}
                      </span>
                      <span className="text-[10px] text-gray-300">
                        {msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <div className={`rounded-xl px-3 py-1.5 text-xs wrap-break-word ${
                      msg.is_mine ? 'bg-blue-600 text-white rounded-tr-sm' : 'bg-white border border-gray-200 text-gray-800 shadow-sm rounded-tl-sm'
                    }`}>
                      {msg.content && msg.content !== msg.file_name && (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                      {msg.file_key && (
                        <div className="mt-1">
                          <p className={`flex items-center gap-1 text-[11px] truncate ${msg.is_mine ? 'text-blue-100' : 'text-gray-600'}`}>
                            <Paperclip className="w-3 h-3 shrink-0" /> {msg.file_name || 'Attachment'}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <button
                              onClick={() => openFile(msg, 'inline')}
                              className={`flex items-center gap-1 text-[11px] font-medium ${msg.is_mine ? 'text-blue-100 hover:text-white' : 'text-blue-600 hover:text-blue-800'}`}
                            >
                              <Eye className="w-3 h-3" /> View
                            </button>
                            <button
                              onClick={() => openFile(msg, 'attachment')}
                              className={`flex items-center gap-1 text-[11px] font-medium ${msg.is_mine ? 'text-blue-100 hover:text-white' : 'text-blue-600 hover:text-blue-800'}`}
                            >
                              <Download className="w-3 h-3" /> Download
                            </button>
                            {/* The file is already here. This submits it as a deliverable
                                without a second upload, so the gateway's size limit -- which
                                is what stopped a 69 MB deliverable being submitted at all --
                                never comes into it. */}
                            {!readOnly && (
                              <button
                                onClick={() => { setPromote(msg); setPromoteTitle(msg.file_name || ''); setPromoteNote(null) }}
                                className={`flex items-center gap-1 text-[11px] font-medium ${msg.is_mine ? 'text-blue-100 hover:text-white' : 'text-teal-700 hover:text-teal-900'}`}
                                title="Submit this file as a deliverable on this milestone"
                              >
                                <FileUp className="w-3 h-3" /> Use as deliverable
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
            {error && <p className="text-xs text-red-500 text-center">{error}</p>}
            <div ref={bottomRef} />
          </div>

          {/* Input area — hidden when the project is archived (read-only). */}
          {readOnly ? (
            <div className="border-t border-gray-200 bg-gray-50 px-3 py-2 text-[11px] text-gray-400 text-center">
              This project is archived — the conversation is read-only.
            </div>
          ) : (
          <div className="border-t border-gray-200 bg-white">
            {/* Selected-file preview — normal in-flow block right above the input (not
                absolutely positioned, so it shows correctly in this inline chat). */}
            {file && (
              <div className="mx-3 mt-2 bg-blue-50 border border-blue-200 rounded px-2 py-1 text-xs flex items-center gap-1">
                <Paperclip className="w-3 h-3 text-blue-500 shrink-0" />
                <span className="truncate text-blue-700">{file.name}</span>
                <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = '' }} className="ml-auto text-gray-400 hover:text-red-500">×</button>
              </div>
            )}
            {promote && (
              <div className="mx-3 mb-2 rounded-xl border border-teal-200 bg-teal-50 p-3">
                <p className="text-xs font-semibold text-teal-900">Submit as a deliverable</p>
                <p className="text-[11px] text-teal-800 mt-0.5 mb-2">
                  <span className="font-medium">{promote.file_name}</span> is already on the server,
                  so nothing is uploaded again and its size does not matter.
                </p>
                <input
                  value={promoteTitle}
                  onChange={e => setPromoteTitle(e.target.value)}
                  placeholder="What is this deliverable called?"
                  className="w-full text-xs border border-teal-300 rounded-lg px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <div className="flex items-center gap-2">
                  <button onClick={submitFromChat} disabled={promoteBusy || !promoteTitle.trim()}
                          className="text-xs font-medium bg-teal-600 text-white rounded-lg px-3 py-1.5 disabled:opacity-50">
                    {promoteBusy ? 'Submitting…' : 'Submit'}
                  </button>
                  <button onClick={() => setPromote(null)} className="text-xs text-teal-800 hover:underline">Cancel</button>
                </div>
              </div>
            )}
            {promoteNote && (
              <p className={`mx-3 mb-2 text-[11px] rounded-lg px-2 py-1.5 ${promoteNote.ok
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-700 border border-red-200'}`}>{promoteNote.text}</p>
            )}
            <div className="px-3 py-2 flex items-end gap-2">
              <button onClick={() => fileRef.current?.click()} className="text-gray-400 hover:text-blue-600 shrink-0" title={t('t_attach_file')}>
                <Paperclip className="w-4 h-4" />
              </button>
              <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send() } }}
                placeholder={t('ph_reply_enter')}
                rows={1}
                className="flex-1 resize-none text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-400 max-h-20"
              />
              <button
                onClick={send}
                disabled={(!text.trim() && !file) || sending}
                className="p-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 shrink-0"
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
              </button>
            </div>
          </div>
          )}
        </div>
      )}

        {promote && (
          <div className="mx-3 mb-2 rounded-xl border border-teal-200 bg-teal-50 p-3">
            <p className="text-xs font-semibold text-teal-900">Submit as a deliverable</p>
            <p className="text-[11px] text-teal-800 mt-0.5 mb-2">
              <span className="font-medium">{promote.file_name}</span> is already on the server,
              so nothing is uploaded again and its size does not matter.
            </p>
            <input
              value={promoteTitle}
              onChange={e => setPromoteTitle(e.target.value)}
              placeholder="What is this deliverable called?"
              className="w-full text-xs border border-teal-300 rounded-lg px-2 py-1.5 mb-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
            <div className="flex items-center gap-2">
              <button onClick={submitFromChat} disabled={promoteBusy || !promoteTitle.trim()}
                      className="text-xs font-medium bg-teal-600 text-white rounded-lg px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1">
                {promoteBusy && <Loader2 className="w-3 h-3 animate-spin" />} Submit
              </button>
              <button onClick={() => setPromote(null)} className="text-xs text-teal-800 hover:underline">Cancel</button>
            </div>
          </div>
        )}
        {promoteNote && (
          <p className={`mx-3 mb-2 text-[11px] rounded-lg px-2 py-1.5 ${promoteNote.ok
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-700 border border-red-200'}`}>{promoteNote.text}</p>
        )}
      {viewing && (

        <FileViewerModal
          open={!!viewing}
          onClose={() => setViewing(null)}
          fetchUrl={`/chat/file/${viewing.id}`}
          filename={viewing.file_name || 'Attachment'}
          onDownload={() => openFile(viewing, 'attachment')}
        />
      )}
    </div>
  )
}
